// What the `+2` at the end of a recipe's ingredient line opens: the WHOLE price, one ingredient per
// row, in a small popover hung off the chip that was tapped.
//
// The ingredient line is one row tall inside a docked panel, so a three-ingredient recipe routinely
// has room for one chip and a `+2` (see ingredientChipsFor). Short ingredients are drawn first, which
// means the `+N` usually hides only things the player already has -- but on a recipe short of three
// materials with room for one chip, it hides shortfalls too. Either way a tail that cannot be
// expanded is a line admitting it is hiding something and then refusing to say what.
//
// Pure of pixi, like crafting_ingredients.ts it reads and crafting_layout.ts beside it. The drawing
// lives in modern_inventory_dialog.ts; what is checked here is the rows and the arithmetic that keeps
// the box on the panel -- see __tests__/recipe_popover.test.ts.

import { chipText, IngredientCost, isShort, orderedCosts } from "./crafting_ingredients"

// Inside the box.
export const POPOVER_PAD_X = 8
export const POPOVER_PAD_Y = 6
// Clear air between the box and the chip it hangs off.
export const POPOVER_GAP = 4
// How close to the panel's edges the box may sit -- the same margin the hover pill keeps.
export const POPOVER_EDGE_PAD = 4
// Between the title and the first ingredient.
export const POPOVER_TITLE_GAP = 3
// What joins an ingredient to where it comes from, on a row for a material nothing makes.
export const POPOVER_NOTE_SEPARATOR = " — "

/**
 * One row of the popover.
 *
 * `note` is the SOURCE line for a material nothing makes, carried on the row itself rather than in a
 * pill of its own: the inline chip has to borrow the hover pill because it has no room, and a popover
 * that opened a second popover to answer "where do I get this" would be absurd.
 */
export interface PopoverRow {
    text: string
    note?: string
    missing: boolean
    // This material has a recipe, so the row is a jump to it -- the same control the inline chip is.
    jumpable: boolean
}

/**
 * Every ingredient, in the same order the line drew them: short ones first.
 *
 * Deliberately the SAME order rather than the recipe's own. The popover is the line continued -- the
 * player tapped a tail to see the rest of it -- so re-sorting would make them find their place again
 * in a list they were already reading.
 */
export const popoverRows = (cost: IngredientCost[]): PopoverRow[] =>
    orderedCosts(cost || []).map(next => ({
        text: chipText(next),
        missing: isShort(next),
        // Only a SHORT ingredient is a question worth answering, exactly as on the line.
        jumpable: isShort(next) && !!next.craftable,
        note: isShort(next) && !next.craftable ? next.source : undefined,
    }))

/**
 * The box's outer size, from the widest row it has to hold and how tall the rows measured.
 *
 * Takes measured text rather than guessing at a font: the rows are drawn as pixi Texts and only pixi
 * knows how wide "0/2 flint — from the caves" came out.
 */
export const popoverSize = ({
    titleW,
    titleH,
    rowW,
    rowH,
    rows,
}: {
    titleW: number
    titleH: number
    rowW: number
    rowH: number
    rows: number
}): { w: number; h: number } => ({
    w: Math.ceil(Math.max(titleW, rowW)) + POPOVER_PAD_X * 2,
    h: Math.ceil(titleH + POPOVER_TITLE_GAP + rowH * Math.max(0, rows)) + POPOVER_PAD_Y * 2,
})

/**
 * Where the box goes, in the PANEL's coordinates.
 *
 * Hung from the chip's left edge so it grows away from the Craft button on the row's right rather than
 * over it, then pulled back inside the panel -- the `+N` is the last thing on a line that ran out of
 * room, so it is close to the middle of a narrow panel and a long ingredient name makes an overhang
 * the common case rather than the rare one.
 *
 * BELOW the chip by preference: that is the direction the eye is already travelling, the line having
 * just run out. Flipped above only when there is no room below, and then clamped, so a recipe at the
 * bottom of the list still shows its whole price.
 */
export const popoverPlacement = ({
    anchorX,
    anchorY,
    anchorH,
    popW,
    popH,
    panelW,
    panelH,
}: {
    anchorX: number
    anchorY: number
    anchorH: number
    popW: number
    popH: number
    panelW: number
    panelH: number
}): { x: number; y: number } => {
    const maxX = Math.max(POPOVER_EDGE_PAD, panelW - popW - POPOVER_EDGE_PAD)
    const x = Math.round(Math.max(POPOVER_EDGE_PAD, Math.min(maxX, anchorX)))

    const below = anchorY + anchorH + POPOVER_GAP
    const above = anchorY - popH - POPOVER_GAP
    const fitsBelow = below + popH <= panelH - POPOVER_EDGE_PAD
    const fitsAbove = above >= POPOVER_EDGE_PAD
    // Neither side fits: take the preferred one and let the clamp pin it, rather than picking the
    // side that overflows LESS and landing the box somewhere the player did not tap.
    const chosen = fitsBelow || !fitsAbove ? below : above
    const maxY = Math.max(POPOVER_EDGE_PAD, panelH - popH - POPOVER_EDGE_PAD)

    return { x, y: Math.round(Math.max(POPOVER_EDGE_PAD, Math.min(maxY, chosen))) }
}
